export interface ChartData {
  index: string;
  [key: string]: number | string | undefined;
}

export type OperationalDataType = "energy" | "currency";

export interface OperationalSeriesData {
  key: string;
  value: number;
}

export interface OperationalChartDataPoint {
  index: string;
  forecast?: OperationalSeriesData[];
  actual?: OperationalSeriesData[];
  meta_first_day?: string;
  meta_last_day?: string;
}

export const legendKeyColors: Record<string, string> = {
  // Main procurement colors
  "On-site direct from PV": "#00022F",
  "On-site direct from PV and battery": "#00022F",
  "On-site battery discharge from PV": "#2A2C76",
  "On-site peak-shifted by battery": "#a2a6ff",
  "From InRange network": "#2779A7",
  "From other suppliers": "#9f9f9f",
  // Match colors
  Matched: "#2779A7",
  "Total export profile": "#00022F",
  // Peak / Off-Peak colors
  "Peak hour energy supplied from on-site generation": "#00022F",
  "Peak hour energy supplied by on-site peak-shifting by battery": "#a2a6ff",
  "Peak hour energy supplied from InRange Network": "#2779A7",
  "Peak hour energy not supplied from InRange": "#9f9f9f",
  "Off-Peak hour energy not supplied from InRange": "#6f6f6f",
  // Generation vs Demand
  Generation: "#00022F",
  Demand: "#2779A7",
  // SPV performance charts
  Revenue: "#00022F",
  "Actual revenue": "#00022F",
  "Actual revenue (demo)": "#00022F",
  "Forecasted revenue": "#2A2C76",
  "Actual generation": "#00022F",
  "Actual generation (demo)": "#00022F",
  "Forecasted generation": "#2A2C76",
  // Landlord charts
  "Exported to network": "#2779A7",
  // Operational charts
  "Forecast on-site direct from PV": "#2A2C76",
  "Forecast on-site direct from PV and battery": "#2A2C76",
  "Forecast on-site battery discharge from PV": "#5356b2ff",
  "Forecast on-site peak-shifted by battery": "#c9cbffff",
  "Forecast from InRange network": "#9EC4E8",
  "Forecast exported to network": "#9EC4E8",
};

export const getChartLegend = (
  data: ChartData[],
  isNotYetProcuringSite?: boolean
): Record<string, string> => {
  const keys = Object.keys(data[0])
    .filter((key) => key !== "index")
    .filter((key) => !key.startsWith("meta_"));

  const sortedKeys = keys
    .map((key, index) => ({ key, index }))
    .sort((a, b) => {
      const aIsForecast = a.key.indexOf("Forecast") === 0;
      const bIsForecast = b.key.indexOf("Forecast") === 0;
      if (aIsForecast === bIsForecast) return a.index - b.index;
      return aIsForecast ? 1 : -1;
    })
    .map(({ key }) => key);

  return sortedKeys.reduce(
    (acc, key) => {
      if (!legendKeyColors[key]) {
        throw new Error(`No color defined for legend key: ${key}`);
      }
      if (isNotYetProcuringSite && key === "From other suppliers") {
        acc[key] = "#cccccc"; // Lighter grey for "From other suppliers" if not yet procuring
      } else {
        acc[key] = legendKeyColors[key];
      }
      return acc;
    },
    {} as Record<string, string>
  );
};
